import React from 'react';
import { CoachAvailabilityInterval, CoachAvailabilityInput, DayOfWeek } from 'globalTypes';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import CoachingAvailabilityEditDayRow from './CoachingAvailabilityEditDayRow';
import CoachingConstants from 'coaching/CoachingConstants';
import Container from 'components/Container';
import Divider from 'components/Divider';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Label from 'components/Label';
import Select from 'components/Select';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import EventTracking from 'lib/EventTracking';

import { getDayOfWeekIndex } from 'coaching/lib/timeIntervals';

type Props = {
  availabilitiesByDay: CoachAvailabilityInput[];
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  onClickSetAvailability: () => void;
  onAvailabilityChange: (intervals: CoachAvailabilityInterval[], day: DayOfWeek) => void;
};

export default class CoachingAvailabilityEditView extends React.Component<Props> {
  findIntervalsByDay = (dayOfWeek: DayOfWeek) => {
    return this.props.availabilitiesByDay[getDayOfWeekIndex(dayOfWeek)]?.timeSlot.intervals;
  };

  componentDidMount() {
    EventTracking.trackKey('coaching.set_coach_availability.view');
  }

  render() {
    const { timezone, onTimezoneChange, onAvailabilityChange, onClickSetAvailability } = this.props;

    return (
      <Container size="xsmall">
        <Heading serif>Set your normal availability</Heading>
        <Text element="p" lineHeight="large">
          Add the hours you’re available to book sessions for a typical week. For one-offs like
          appointments or vacations, add time off later.
        </Text>
        <FormElement
          label="Time zone"
          onValidChange={data => {
            onTimezoneChange(data);
            EventTracking.trackKey('coaching.set_coach_availability.select_time_zone.click');
          }}
          element={Select}
          elementProps={{
            options: TimezoneHelper.USTimezoneSelectOptions.filter(
              option => !CoachingConstants.TIMEZONES_TO_EXCLUDE.includes(option.label)
            ),
            value: timezone,
            searchable: false,
          }}
        />
        <Label>Availability</Label>
        <Text color="thunder">
          Peak Remind Coaching hours are 5:00 – 9:00 PM across time zones.
        </Text>
        <Divider size="medium" />
        {CoachingConstants.VALID_COACHING_DAYS.map(day => (
          <CoachingAvailabilityEditDayRow
            key={day}
            dayOfWeek={day}
            initialIntervals={this.findIntervalsByDay(day)}
            onAvailabilityChange={(intervals: CoachAvailabilityInterval[], day: DayOfWeek) =>
              onAvailabilityChange(intervals, day)
            }
          />
        ))}
        <Buttons full>
          <Button
            data-test="set-availability-button"
            eventKey="coaching.set_coach_availability.set_availability.click"
            onClick={onClickSetAvailability}>
            Set availability
          </Button>
        </Buttons>
      </Container>
    );
  }
}
